import { useCallback, useState, useEffect, useRef } from "react";
import { Asset, Item, copy } from "@thenounproject/lingo-core";
import useTrackColorUsage from "@redux/actions/analytics/trackColorUsage";

export type ColorFormat = "hex" | "rgb" | "hsb" | "pms" | "cmyk";

export type ColorCopyState = {
  format: ColorFormat | null;
  value: string | null;
  hasCopied: boolean;
};

export function useColorCopy(asset: Asset, item: Item) {
  const [copyState, setCopyState] = useState<ColorCopyState>({
    format: null,
    value: null,
    hasCopied: false,
  });
  const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const trackColorUsage = useTrackColorUsage();

  useEffect(() => {
    return () => {
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    };
  }, []);

  const copyColor = useCallback(
    (format: ColorFormat) => {
      const color = asset.colors?.[0];
      if (!color || !color[format]) {
        console.warn(`Color format "${format}" not available for this asset`);
        return;
      }

      const value = color[format] as string;

      // Track analytics if item is provided
      if (item) {
        trackColorUsage({ item, format });
      }

      let didCopy = false;
      try {
        didCopy = copy(value);
      } catch (err) {
        console.error("Clipboard error:", err);
      }
      if (didCopy) {
        if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
        setCopyState({ format, value, hasCopied: true });
        resetTimerRef.current = setTimeout(() => {
          setCopyState({ format: null, value: null, hasCopied: false });
        }, 2000);
      }
    },
    [asset.colors, item, trackColorUsage]
  );

  const getColorValue = useCallback(
    (format: ColorFormat): string | null => {
      const color = asset.colors?.[0];
      return color?.[format] ? (color[format] as string) : null;
    },
    [asset.colors]
  );

  const isFormatAvailable = useCallback(
    (format: ColorFormat): boolean => {
      const color = asset.colors?.[0];
      return Boolean(color?.[format]);
    },
    [asset.colors]
  );

  return {
    copyColor,
    copyState,
    getColorValue,
    isFormatAvailable,
    lastCopiedFormat: copyState.hasCopied ? copyState.format : null,
  };
}
